import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { Patient, Prescription, PrescriptionGroup } from "common/models/types";
import PatientList from "components/organisms/PatientList";
import PatientInfo from "components/organisms/PatientInfo";
import PatientForm from "components/organisms/PatientForm";

interface PatientsTemplateProps {
  patients: Patient[];
  selectedPatient: Patient | null;
  onSelectPatient: (patient: Patient) => void;
  onUpdatePatient?: (patient: Patient) => void;
  onSelectPatientChange?: (patient: Patient | null) => void;
  onReloadPatient?: (patientId: number) => void;
  onCreatePatient?: (patientData: {
    birthYear: string;
    gender: string;
    memo: string;
  }) => void;
  onSearch: (search?: string) => void;
  // 무한 스크롤 관련 props
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  isLoadingDetail?: boolean;
  onDeletePatient?: (patientId: number) => void;
  // 처방전 관련 props
  onEditPrescription?: (prescription: Prescription) => void;
  onCancelEditPrescription?: () => void;
  onSavePrescription?: (prescription: Prescription) => void;
  onDeletePrescription?: (prescriptionId: number) => void;
  editingPrescription?: Prescription | null;
  isPrescriptionLoading?: boolean;
  // 처방전 그룹 관련 props (추가)
  editingPrescriptionGroup?: PrescriptionGroup | null;
  onEditPrescriptionGroup?: (
    groupId: string,
    prescriptionDate: Date,
    prescriptions: Prescription[]
  ) => void;
  onCancelEditPrescriptionGroup?: () => void;
  onSavePrescriptionGroup?: (group: PrescriptionGroup) => void;
  onDeletePrescriptionGroup?: (groupId: string, prescriptions: Prescription[]) => void;
  onAddPrescriptionItem?: () => void;
  onUpdatePrescriptionItem?: (prescription: Prescription, index: number) => void;
  onDeletePrescriptionItem?: (index: number) => void;
  onUpdatePrescriptionDate?: (date: Date) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const ListSection = styled.div`
  width: 360px;
  flex-shrink: 0;
  border-right: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  overflow: hidden;
`;

const DetailSection = styled.div`
  flex-grow: 1;
  padding: 24px;
  overflow: hidden;
`;

const PatientsTemplate: React.FC<PatientsTemplateProps> = ({
  patients,
  selectedPatient,
  onSelectPatient,
  onUpdatePatient,
  onSelectPatientChange,
  onReloadPatient,
  onCreatePatient,
  onSearch,
  hasMore,
  isLoadingMore,
  onLoadMore,
  isLoadingDetail,
  onDeletePatient,
  onEditPrescription,
  onCancelEditPrescription,
  onSavePrescription,
  onDeletePrescription,
  editingPrescription,
  isPrescriptionLoading,
  onEditPrescriptionGroup,
  onCancelEditPrescriptionGroup,
  onSavePrescriptionGroup,
  onDeletePrescriptionGroup,
  onAddPrescriptionItem,
  onUpdatePrescriptionItem,
  onDeletePrescriptionItem,
  onUpdatePrescriptionDate,
}) => {
  return (
    <Container>
      <ListSection>
        <PatientList
          patients={patients}
          selectedPatient={selectedPatient}
          onSelectPatient={onSelectPatient}
          onCreatePatient={onCreatePatient}
          onSearch={onSearch}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
        />
      </ListSection>
      <DetailSection>
        <PatientInfo
          patient={selectedPatient}
          onSelect={onSelectPatientChange}
          onReload={onReloadPatient}
          isLoading={isLoadingDetail}
          onDelete={onDeletePatient}
          isPrescriptionLoading={isPrescriptionLoading}
          onSavePatient={onUpdatePatient}
          onSavePrescriptionGroup={onSavePrescriptionGroup}
          onDeletePrescriptionGroup={onDeletePrescriptionGroup}
        />
      </DetailSection>
    </Container>
  );
};

export default PatientsTemplate;
